// The inventory panel (I): your resource stacks — name, class, units,
// stat chips. Cold state over HTTP; refetched each open.

import { escapeHtml } from "./escape";
import { statChips } from "./survey";

interface StackInfo {
  location: string;
  spawnId: string;
  name: string;
  className: string;
  branch: string;
  stats: Record<string, number>;
  units: number;
}

export interface TradeBridge {
  /** Who is close enough to hand something to, right now. */
  peopleWithinReach(): Array<{ sessionId: string; name: string }>;
  offerUnits(toSessionId: string, spawnId: string, units: number): void;
  offerReading(toSessionId: string, readingId: string): void;
  offerRouteReading(toSessionId: string, readingId: string): void;
}

interface RouteReadingInfo {
  id: string;
  regionLabel: string;
  scoutName: string;
  lines: string[];
  age: string;
}

interface ReadingInfo {
  id: string;
  resourceName: string;
  className: string;
  x: number;
  z: number;
  best: number;
  surveyorName: string;
  mine: boolean;
}

export class InventoryPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;

  constructor(
    parent: HTMLElement,
    private trade?: TradeBridge,
  ) {
    this.root = document.createElement("div");
    this.root.className = "panel-float inventory-panel";
    this.root.innerHTML = `
      <div class="panel-title">HOLD <span class="panel-hint">I to close</span></div>
      <div class="inventory-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".inventory-body") as HTMLDivElement;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">checking the hold…</div>`;
    try {
      const res = await fetch("/api/inventory");
      if (!res.ok) throw new Error(String(res.status));
      const bodyJson = (await res.json()) as {
        stacks: StackInfo[];
        carried: number;
        capacity: number;
      };
      let readings: ReadingInfo[] = [];
      let routeReadings: RouteReadingInfo[] = [];
      try {
        const r = await fetch("/api/readings");
        if (r.ok) {
          const payload = (await r.json()) as {
            readings: ReadingInfo[];
            routeReadings?: RouteReadingInfo[];
          };
          readings = payload.readings;
          routeReadings = payload.routeReadings ?? [];
        }
      } catch {
        // The hold still reads fine without them.
      }
      this.render(bodyJson.stacks, bodyJson.carried, bodyJson.capacity, readings, routeReadings);
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the manifest is smudged (try again)</div>`;
    }
  }

  private render(
    stacks: StackInfo[],
    carried: number,
    capacity: number,
    readings: ReadingInfo[] = [],
    routeReadings: RouteReadingInfo[] = [],
  ): void {
    this.body.innerHTML = "";
    const full = capacity > 0 && carried >= capacity;
    const gauge = document.createElement("div");
    gauge.className = `carry-gauge${full ? " full" : ""}`;
    gauge.innerHTML = `
      <div class="carry-bar"><div class="carry-fill" style="width:${Math.min(100, (carried / Math.max(1, capacity)) * 100).toFixed(1)}%"></div></div>
      <span class="carry-label">${full ? "hold full — stow something" : "carrying"}</span>
    `;
    this.body.append(gauge);

    if (stacks.length === 0 && readings.length === 0) {
      this.body.insertAdjacentHTML(
        "beforeend",
        `<div class="panel-empty">empty. survey something (R) and sample it.</div>`,
      );
      return;
    }

    for (const where of ["hold", "locker"] as const) {
      const group = stacks.filter((s) => s.location === where);
      if (group.length === 0) continue;
      const heading = document.createElement("div");
      heading.className = "stack-heading";
      heading.textContent = where === "hold" ? "ON YOU" : "IN THE LOCKER";
      this.body.append(heading);

      for (const stack of group) {
        const row = document.createElement("div");
        row.className = "stack-row";
        const action = where === "hold" ? "stow" : "take";
        row.innerHTML = `
          <span class="spawn-name">${escapeHtml(stack.name)}</span>
          <span class="spawn-class">${escapeHtml(stack.className)}</span>
          <span class="stack-units">${stack.units.toLocaleString()} u</span>
          <span class="spawn-stats">${statChips(stack.stats)}</span>
          <button class="stack-move">${action}</button>
          ${where === "hold" && this.trade ? '<button class="stack-give">give</button>' : ""}
        `;
        const button = row.querySelector(".stack-move") as HTMLButtonElement;
        button.addEventListener("click", () => {
          button.disabled = true;
          void this.move(stack.spawnId, stack.units, where === "hold" ? "locker" : "hold");
        });
        const give = row.querySelector(".stack-give") as HTMLButtonElement | null;
        give?.addEventListener("click", () =>
          this.showGivePicker(row, (sessionId) =>
            this.trade?.offerUnits(sessionId, stack.spawnId, stack.units),
          ),
        );
        this.body.append(row);
      }
    }

    if (readings.length > 0) {
      const heading = document.createElement("div");
      heading.className = "stack-heading";
      heading.textContent = "SEALED READINGS";
      this.body.append(heading);

      for (const reading of readings) {
        const row = document.createElement("div");
        row.className = "stack-row";
        const where = `${reading.x >= 0 ? "+" : ""}${reading.x}, ${reading.z >= 0 ? "+" : ""}${reading.z}`;
        row.innerHTML = `
          <span class="spawn-name">${escapeHtml(reading.resourceName)}</span>
          <span class="spawn-class">${escapeHtml(reading.className)}</span>
          <span class="reading-where">${where}</span>
          <span class="spawn-stats"><span class="stat-chip">read by ${escapeHtml(reading.surveyorName)}</span></span>
          ${this.trade ? '<button class="stack-give">give</button>' : ""}
        `;
        const give = row.querySelector(".stack-give") as HTMLButtonElement | null;
        give?.addEventListener("click", () =>
          this.showGivePicker(row, (sessionId) => this.trade?.offerReading(sessionId, reading.id)),
        );
        this.body.append(row);
      }
    }

    if (routeReadings.length > 0) {
      const heading = document.createElement("div");
      heading.className = "stack-heading";
      heading.textContent = "ROUTE READINGS";
      this.body.append(heading);

      // Words a holder reads — never a map, never a coordinate. Consult
      // it standing on the ground it describes (/consult) and the card
      // proves itself.
      for (const reading of routeReadings) {
        const row = document.createElement("div");
        row.className = "stack-row route-reading-row";
        row.innerHTML = `
          <span class="spawn-name">the ground about ${escapeHtml(reading.regionLabel)}</span>
          <span class="spawn-class">${escapeHtml(reading.age)}</span>
          <span class="spawn-stats"><span class="stat-chip">cut by ${escapeHtml(reading.scoutName)}</span></span>
          ${this.trade ? '<button class="stack-give">give</button>' : ""}
          <div class="route-reading-lines">${reading.lines
            .map((line) => `<div>${escapeHtml(line)}</div>`)
            .join("")}</div>
        `;
        const give = row.querySelector(".stack-give") as HTMLButtonElement | null;
        give?.addEventListener("click", () =>
          this.showGivePicker(row, (sessionId) =>
            this.trade?.offerRouteReading(sessionId, reading.id),
          ),
        );
        this.body.append(row);
      }
    }
  }

  /**
   * Hand a stack to somebody standing with you. The list is whoever is
   * actually in reach — you cannot mail things across the planet, which is
   * the whole point of a hold and a commute.
   */
  private showGivePicker(row: HTMLElement, offer: (sessionId: string) => void): void {
    row.querySelector(".give-picker")?.remove();
    const people = this.trade?.peopleWithinReach() ?? [];
    const picker = document.createElement("div");
    picker.className = "give-picker";
    if (people.length === 0) {
      picker.innerHTML = `<span class="panel-empty">nobody within reach</span>`;
      row.append(picker);
      window.setTimeout(() => picker.remove(), 2500);
      return;
    }
    for (const person of people) {
      const button = document.createElement("button");
      button.className = "give-target";
      button.textContent = person.name;
      button.addEventListener("click", () => {
        offer(person.sessionId);
        picker.remove();
      });
      picker.append(button);
    }
    row.append(picker);
  }

  /** Stow to (or take from) the settlement locker, then re-read the truth. */
  private async move(spawnId: string, units: number, to: "hold" | "locker"): Promise<void> {
    try {
      await fetch("/api/inventory/move", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ spawnId, units, to }),
      });
    } finally {
      await this.refresh();
    }
  }
}
